import React, { useMemo } from 'react';

import { BaseGym, Product } from '../../types/Gym';

import { track } from '../../services/analytics';

import { Button, Grid, Row, WebpImageWithFallback } from '@husslehq/ui-library';

import fakeDoorList from '../../pages/fake-door-list.json';

import styles from '../../components/PassPicker/PassPicker.module.scss';

type Props = {
  products: Product[];
  gym: BaseGym;
  onSelectProduct: (productType: string) => void;
  onShowProducts: (productsShown: boolean) => void;
};

export const PassPicker: React.FC<Props> = ({ products, gym, onSelectProduct, onShowProducts }) => {
  const assetPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX ?? '';
  const dailyProduct = useMemo(() => products && products.find((product) => product.type === 'daypass'), [products]);
  const monthlyProduct = useMemo(() => products && products.find((product) => product.type === 'monthly'), [products]);
  const directProduct = useMemo(() => products && products.find((product) => product.type === 'direct'), [products]);
  const fakeDoorEntry = fakeDoorList.find((gymEntry) => gymEntry.GymId === parseInt(gym.id, 10));

  const fakeDoorPrice = !!fakeDoorEntry && fakeDoorEntry.DirectMembershipPrice * 100;

  const dailyPrice =
    dailyProduct && dailyProduct.discount ? dailyProduct.discount.discounted_price : dailyProduct?.price || undefined;

  const monthlyPlusPrice =
    monthlyProduct &&
    monthlyProduct.discount &&
    monthlyProduct.discount.discounted_price > 0 &&
    !monthlyProduct.discount.trial_days
      ? monthlyProduct.discount.discounted_price
      : monthlyProduct?.price;

  const directPrice = directProduct ? directProduct.price : fakeDoorPrice;

  const monthlyPrice = Math.min(...[monthlyPlusPrice, directPrice].filter((item): item is number => !!item));

  const passData = [
    {
      type: 'monthly',
      name: 'Monthly',
      price: monthlyPrice,
      image: assetPrefix + '/img/picker-month.jpg',
      imageAlt: 'People in a gym exercising with weights',
      desc: 'Ongoing access to one or multiple gyms',
      terms: 'Cancel anytime',
    },
    {
      type: 'daily',
      name: 'Day Passes',
      price: dailyPrice,
      image: assetPrefix + '/img/picker-day.jpg',
      imageAlt: 'Person walking with a gym bag over their shoulder',
      desc: 'Pass for a single gym for 1 visit',
      terms: 'Valid for 30 days',
    },
  ];

  return (
    <div>
      <h2 className={styles.picker__title}>Access options at this gym</h2>
      <Grid>
        <Row className={styles.picker__row}>
          {passData.map((pass, index) => (
            <div
              key={index}
              className={styles.picker__item}
              onClick={() => {
                onShowProducts(true);
                onSelectProduct(pass.type);
                track({
                  event: 'gym_spa',
                  category: 'Pass Picker',
                  action: `View ${pass.type} passes`,
                  label: gym.name,
                });
              }}
            >
              <div className={styles.picker}>
                <div className={styles['picker__name-container']}>
                  <h3 className={styles.picker__name}>{pass.name}</h3>
                  <span className={styles.picker__from}>
                    from{' '}
                    <span className={styles.picker__price}>
                      {pass.price !== undefined && pass.price > 0 ? (
                        <>
                          &pound;<span className={styles.picker__pounds}>{Math.floor(pass.price / 100)}</span>.
                          {((pass.price / 100) % 1).toFixed(2).substring(2)}
                        </>
                      ) : (
                        <span className={styles.picker__pounds}>FREE</span>
                      )}
                    </span>
                  </span>
                </div>
                <WebpImageWithFallback className={styles.picker__img} src={pass.image} alt={pass.imageAlt} />
                <p className={styles.picker__desc}>{pass.desc}</p>
                <p className={styles.picker__terms}>{pass.terms}</p>
                <Button
                  level="primary"
                  className={styles.picker__btn}
                  onClick={() => {
                    onShowProducts(true);
                    onSelectProduct(pass.type);
                  }}
                >
                  Choose {pass.type === 'monthly' ? 'Monthly' : 'a Day Pass'}
                </Button>
              </div>
            </div>
          ))}
        </Row>
      </Grid>
    </div>
  );
};
